import React from 'react';
import { Link } from 'react-router-dom';
import { styled } from '@mui/system';
import Typography from '@mui/material/Typography';
import { useTranslate } from 'hooks/useTranslate';
import { API_URL } from 'services/api';
import getLocale, { getLocalizedPath } from 'helpers/getLocale';
import getCountry from 'helpers/getCountry';
import { getProfessionSlug, getBaseUrl } from 'utils/seo';
import Helmet from 'components/Helmet';
import Header from 'components/Header';
import Footer from 'components/Footer';
import MainLayout from 'layouts/MainLayout';
import Preloader from 'components/Preloader';

const PAGE_SIZE = 100;

const Wrapper = styled('section')(({ theme }) => ({
  padding: '40px 100px',
  ...theme.wrapper,
  boxSizing: 'border-box',
  minHeight: 'calc(100vh - 235px)',
  [theme.breakpoints.down('lg')]: { padding: 50 },
  [theme.breakpoints.down('md')]: { padding: 30 },
  [theme.breakpoints.down('sm')]: { padding: 20 }
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: 16,
  color: theme.palette.textPrimary.main
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  marginBottom: 32,
  color: theme.palette.textSecondary?.main || theme.palette.text?.secondary
}));

const List = styled('ul')(() => ({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
  gap: '8px 24px'
}));

const Item = styled('li')(({ theme }) => ({
  fontSize: 16,
  lineHeight: '24px',
  '& a': {
    color: theme.palette.textPrimary?.main || 'inherit',
    textDecoration: 'none',
    borderBottom: '1px solid transparent',
    transition: 'border-color 0.15s ease'
  },
  '& a:hover': {
    borderBottomColor: 'currentColor'
  }
}));

async function fetchAllProfessions(signal) {
  const all = [];
  let offset = 0;
  // Cap iterations defensively in case the count is missing.
  const MAX_PAGES = 50;
  // Use the same locale headers as services/api. Hardcoding 'uk'/'UA' here
  // returned an empty set (the API speaks 'ua'/'en', not ISO 'uk'), which is
  // why this page rendered "(0)" on both brands.
  const localeHeaders = {
    'X-API-Language': getLocale(),
    'X-API-Country': getCountry()
  };
  for (let i = 0; i < MAX_PAGES; i++) {
    const url = `${API_URL}professions?limit=${PAGE_SIZE}&offset=${offset}`;
    const resp = await fetch(url, {
      signal,
      headers: localeHeaders
    });
    if (!resp.ok) break;
    const data = await resp.json();
    const items = (data && data.data) || [];
    if (items.length === 0) break;
    all.push(...items);
    const total = (data && data.meta && data.meta.count) || 0;
    offset += PAGE_SIZE;
    if (items.length < PAGE_SIZE || (total && offset >= total)) break;
  }
  return all;
}

const ProfessionsAll = () => {
  const t = useTranslate('HELMET');
  const [professions, setProfessions] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    const controller = new AbortController();
    fetchAllProfessions(controller.signal)
      .then((items) => {
        items.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'uk'));
        setProfessions(items);
        setLoaded(true);
      })
      .catch(() => {
        setLoaded(true);
      });
    return () => controller.abort();
  }, []);

  // Render <Helmet> only after data resolved so SEO tags reflect the real
  // count and the prerender service captures the full list of <a href> links.
  if (!loaded) return <Preloader />;

  // Brand-aware: hardcoding profitroll emitted a wrong canonical on jobalto.
  const canonicalUrl = `${getBaseUrl()}${getLocalizedPath('/professions/all')}`;

  return (
    <>
      <Helmet
        page="professionsAll"
        meta={{
          title: t('professionsAllTitle'),
          description: t('professionsAllDescription'),
          ogTitle: t('professionsAllOgTitle'),
          ogDescription: t('professionsAllOgDescription')
        }}
        canonicalUrl={canonicalUrl}
        includeHreflang={true}
      />
      <MainLayout>
        <Header />
        <Wrapper>
          <Title variant="h1">{t('professionsAllH1')}</Title>
          <Subtitle variant="body1">
            {t('professionsAllSubtitle', { count: professions.length })}
          </Subtitle>
          <List>
            {professions.map((p) => {
              if (!p || !p._id) return null;
              const slug = getProfessionSlug(p);
              if (!slug || slug === 'profession') return null;
              return (
                <Item key={p._id}>
                  <Link to={getLocalizedPath(`/professions/${slug}/${p._id}`)}>
                    {p.name}
                  </Link>
                </Item>
              );
            })}
          </List>
        </Wrapper>
        <Footer />
      </MainLayout>
    </>
  );
};

export default ProfessionsAll;
